// ---------------------------------------------------------------------------
// richTables — table support for RichEditor (Spencer: event notes are
// structured tables; the editor's plain-text paste flattened his Google Docs
// grids to word soup). Pure DOM helpers, no React: insert / Tab-navigate /
// mutate tables inside the contentEditable, plus the paste sanitizer that
// lets a rich <table> through the plain-text-paste discipline.
//
// Node-safety: nothing here touches document/window at MODULE scope (client
// unit tests run under plain Node). Every function reaches the DOM through
// the nodes it's handed (ownerDocument / defaultView) or guards the global.
// ---------------------------------------------------------------------------

/** The 3×3 starter grid: header row + 2 body rows, every cell a <br> so it has
 *  a caret line. `data-cgt-new` is an EPHEMERAL locator — execCommand gives no
 *  handle back, so the editor finds the table by it and strips it immediately;
 *  it never reaches a save. Trailing <p> = the escape hatch below the table. */
export function tableTemplateHtml(): string {
  const head = `<tr>${'<th><br></th>'.repeat(3)}</tr>`;
  const body = `<tr>${'<td><br></td>'.repeat(3)}</tr>`.repeat(2);
  return `<table data-cgt-new="1"><thead>${head}</thead><tbody>${body}</tbody></table><p><br></p>`;
}

/** The td/th the selection sits in, or null when it isn't in a table that
 *  belongs to `root` — the guard that keeps Tab/Enter/right-click stock
 *  everywhere else in the editor. */
export function findCell(node: Node | null, root: HTMLElement | null): HTMLTableCellElement | null {
  if (!node || !root) return null;
  const el = node.nodeType === 1 /* ELEMENT_NODE */ ? (node as HTMLElement) : node.parentElement;
  const cell = el?.closest('td, th') ?? null;
  return cell && root.contains(cell) ? (cell as HTMLTableCellElement) : null;
}

/** Collapse the selection to the start of a cell (before its <br>). The caller
 *  owns focusing the editor first — a range set on a blurred host is invisible. */
export function placeCaret(cell: HTMLElement): void {
  const doc = cell.ownerDocument;
  const sel = doc.defaultView?.getSelection();
  if (!sel) return;
  const r = doc.createRange();
  r.selectNodeContents(cell);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
}

/** Enter inside a cell = a line break INSIDE the cell — never the browser's
 *  default block-splitting, which can shear a table apart. */
export function insertLineBreakInCell(cell: HTMLTableCellElement): void {
  const doc = cell.ownerDocument;
  const sel = doc.defaultView?.getSelection();
  if (!sel?.rangeCount) return;
  const r = sel.getRangeAt(0);
  r.deleteContents();
  const br = doc.createElement('br');
  r.insertNode(br);
  // A lone <br> at the very end of a block doesn't render a new line — give the
  // caret a real line to land on. The spare is reused by the next press, so
  // trailing breaks never accumulate past one.
  if (!br.nextSibling) br.after(doc.createElement('br'));
  const after = doc.createRange();
  after.setStartAfter(br);
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);
}

/** Tab / Shift+Tab target. Cells in document order; forward from the LAST cell
 *  grows the table by one row (the "keep typing the next event" flow), so the
 *  caller must fire onInput when `grewRow` comes back true. Backward from the
 *  first cell returns null — the caller holds the caret still. */
export function nextCellOnTab(
  cell: HTMLTableCellElement,
  backward: boolean,
): { cell: HTMLTableCellElement | null; grewRow: boolean } {
  const table = cell.closest('table');
  if (!table) return { cell: null, grewRow: false };
  const cells = Array.from(table.querySelectorAll<HTMLTableCellElement>('td, th'))
    .filter((c) => c.closest('table') === table); // pasted content can nest tables — stay in ours
  const i = cells.indexOf(cell);
  if (i < 0) return { cell: null, grewRow: false };
  if (backward) return { cell: cells[i - 1] ?? null, grewRow: false };
  if (i < cells.length - 1) return { cell: cells[i + 1], grewRow: false };
  const rows = ownRows(table);
  const last = rows[rows.length - 1];
  if (!last) return { cell: null, grewRow: false };
  const tr = blankRow(last.ownerDocument, last.cells.length);
  last.after(tr);
  return { cell: tr.cells[0] ?? null, grewRow: true };
}

export function addRow(cell: HTMLTableCellElement, where: 'above' | 'below'): HTMLTableCellElement | null {
  const row = cell.closest('tr');
  const table = cell.closest('table');
  if (!row || !table) return null;
  const tr = blankRow(row.ownerDocument, row.cells.length);
  // "Below" a header row means "first body row" when a tbody exists — a td row
  // appended inside <thead> renders fine today and reads wrong forever after.
  if (where === 'below' && row.parentElement?.tagName === 'THEAD') {
    const tbody = table.querySelector('tbody');
    if (tbody) tbody.insertBefore(tr, tbody.firstChild);
    else row.after(tr);
  } else if (where === 'below') row.after(tr);
  else row.before(tr);
  return tr.cells[0] ?? null;
}

/** Column math is positional (cellIndex), which reads straight through our own
 *  grids; colspan-heavy pasted tables may land a column off-by-one — acceptable,
 *  the menu's delete ops recover. New cells match their row's flavor (th/td). */
export function addColumn(cell: HTMLTableCellElement, where: 'left' | 'right'): HTMLTableCellElement | null {
  const row = cell.closest('tr');
  const table = cell.closest('table');
  if (!row || !table) return null;
  const at = where === 'left' ? cell.cellIndex : cell.cellIndex + 1;
  let focus: HTMLTableCellElement | null = null;
  for (const tr of ownRows(table)) {
    const doc = tr.ownerDocument;
    const c = doc.createElement(tr.cells[0]?.tagName === 'TH' ? 'th' : 'td') as HTMLTableCellElement;
    c.appendChild(doc.createElement('br'));
    tr.insertBefore(c, tr.cells[Math.min(at, tr.cells.length)] ?? null);
    if (tr === row) focus = c;
  }
  return focus;
}

export function deleteRow(cell: HTMLTableCellElement): HTMLTableCellElement | null {
  const row = cell.closest('tr');
  const table = cell.closest('table');
  if (!row || !table) return null;
  const idx = cell.cellIndex;
  const rows = ownRows(table);
  const i = rows.indexOf(row);
  const neighbor = rows[i + 1] ?? rows[i - 1] ?? null;
  row.remove();
  if (!table.querySelector('td, th')) { table.remove(); return null; }
  return neighbor ? (neighbor.cells[Math.min(idx, neighbor.cells.length - 1)] ?? null) : null;
}

export function deleteColumn(cell: HTMLTableCellElement): HTMLTableCellElement | null {
  const row = cell.closest('tr');
  const table = cell.closest('table');
  if (!row || !table) return null;
  const idx = cell.cellIndex;
  for (const tr of ownRows(table)) {
    tr.cells[idx]?.remove();
    if (tr.cells.length === 0) tr.remove(); // a row of nothing is a border artifact
  }
  if (!table.querySelector('td, th')) { table.remove(); return null; }
  if (!row.isConnected) return null;
  return row.cells[Math.min(idx, row.cells.length - 1)] ?? null;
}

/** Removes the table and parks the caret where it stood, so typing resumes in
 *  the surrounding flow instead of snapping to the top of the editor. */
export function deleteTable(cell: HTMLTableCellElement): void {
  const table = cell.closest('table');
  if (!table) return;
  const doc = table.ownerDocument;
  const sel = doc.defaultView?.getSelection();
  const r = doc.createRange();
  r.setStartBefore(table);
  r.collapse(true);
  table.remove();
  if (sel) { sel.removeAllRanges(); sel.addRange(r); }
}

/* ------------------------------------------------------------------ */
/* Paste sanitizer — the FIRST net. The serialize-time style scrub in   */
/* getHtml is the second, and the server's sanitizeHtml the third.      */
/* ------------------------------------------------------------------ */

// The whole allow-list. Everything else is unwrapped to its children (text
// survives, structure doesn't); DROP tags take their contents with them.
const KEEP = new Set([
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
  'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'UL', 'OL', 'LI', 'A',
]);
const DROP = new Set([
  'SCRIPT', 'STYLE', 'TITLE', 'META', 'LINK', 'BASE', 'IFRAME', 'OBJECT', 'EMBED',
  'SVG', 'MATH', 'TEMPLATE', 'NOSCRIPT', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT',
]);

/** Sanitize clipboard HTML down to structure for the table paste-through.
 *  Returns null when nothing tabular survives (a stray "<table" in a code
 *  snippet, say) — the caller falls back to today's plain-text paste. */
export function sanitizePastedHtml(html: string): string | null {
  if (typeof DOMParser === 'undefined') return null; // not a browser — no rich path
  const doc = new DOMParser().parseFromString(html, 'text/html'); // inert: scripts never run
  scrub(doc.body);
  if (!doc.body.querySelector('table td, table th')) return null;
  // Escape hatch: a paste ENDING in a table would otherwise wall off the caret.
  if (doc.body.lastElementChild?.tagName === 'TABLE') {
    doc.body.insertAdjacentHTML('beforeend', '<p><br></p>');
  }
  return doc.body.innerHTML;
}

function scrub(root: HTMLElement): void {
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === 8 /* COMMENT_NODE */) { node.remove(); continue; }
    if (node.nodeType !== 1 /* ELEMENT_NODE */) continue; // text stays as-is
    const el = node as HTMLElement;
    if (DROP.has(el.tagName)) { el.remove(); continue; }
    scrub(el); // depth-first, so an unwrap splices already-clean children
    // Google Docs wraps the ENTIRE clipboard in <b style="font-weight:normal"
    // id="docs-internal-guid-…"> — keep that <b> and stripping its style would
    // bold Spencer's whole paste. It's a wrapper, not bold: unwrap it.
    const gdocsWrapper =
      (el.tagName === 'B' || el.tagName === 'STRONG') &&
      (/font-weight:\s*normal/i.test(el.getAttribute('style') ?? '') || el.id.startsWith('docs-internal-guid'));
    if (!KEEP.has(el.tagName) || gdocsWrapper) {
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    // Allow-listed: strip EVERY attribute except a.href (http(s) only, so
    // javascript:/data: die here) and td/th colspan+rowspan (plain integers).
    for (const name of el.getAttributeNames()) {
      const value = el.getAttribute(name)?.trim() ?? '';
      const keep =
        (el.tagName === 'A' && name === 'href' && /^https?:\/\//i.test(value)) ||
        ((el.tagName === 'TD' || el.tagName === 'TH') && (name === 'colspan' || name === 'rowspan') && /^\d+$/.test(value));
      if (!keep) el.removeAttribute(name);
    }
    if (el.tagName === 'A' && !el.hasAttribute('href')) {
      el.replaceWith(...Array.from(el.childNodes)); // a link that lost its destination is just text
    }
  }
}

/* ------------------------------------------------------------------ */

function ownRows(table: HTMLTableElement): HTMLTableRowElement[] {
  return Array.from(table.querySelectorAll('tr')).filter((tr) => tr.closest('table') === table);
}

function blankRow(doc: Document, cells: number): HTMLTableRowElement {
  const tr = doc.createElement('tr');
  for (let i = 0; i < Math.max(1, cells); i++) {
    const td = doc.createElement('td');
    td.appendChild(doc.createElement('br'));
    tr.appendChild(td);
  }
  return tr;
}
